import { Logout, Notifications, Policy, Settings, Work } from "@mui/icons-material";
import { Avatar, Box, Button, Divider, Link, ListItemIcon, ListItemText, Menu, MenuItem, Slide, Tab, Tabs, Toolbar } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import * as React from "react";
import { memo, SyntheticEvent, useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { Base } from "../../framework/base";
import { customHistory } from "../../framework/customHistory";
import { IEmployeeEdit } from "../../models/employee/employeeEdit";
import { IEmployeeParameters } from "../../models/employee/employeeIParameters";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import { Session } from "../../models/session/session";
import { ISessionState } from "../../models/session/sessionState";
import * as employeeService from "../../services/employeeService";
import { OwnerSettingsDialog } from "../owner/ownerSettingsDialog";
import { OwnSettingsDialog } from "../user/ownSettingsDialog";
import { Link as ReactLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BetaLabel } from "./betaLabel";
import { WorkShiftPlanningTab } from "../workShiftPlanning/WorkShiftPlanningRouter";
import { LinkIconButton } from "../framework/linkButton";

interface Page {
    title: string;
    path: string;
    exact?: boolean;
    isVisible?: () => boolean;
    children?: Page[];
    target?: string;
    isBeta?: boolean;
}

export interface Props {
    setEmployeeParameters: (parameters: IEmployeeParameters, saveToDb: boolean) => void;
    onLogout: () => void;
    showMenu?: boolean;
}

export const Header = memo(({ setEmployeeParameters, onLogout }: Props) => {
    const [sessionState, setSessionState] = useState<ISessionState>();
    const [userNames, setUserNames] = useState<string[]>([]);
    const [pages, setPages] = useState<Page[]>();
    const [hasPrivacyPolicy, setHasPrivacyPolicy] = useState<boolean>(false);
    const [employeeEdit, setEmployeeEdit] = useState<IEmployeeEdit>();
    const [showOwnSettingsDialog, setShowOwnSettingsDialog] = useState<boolean>(false);
    const [showOwnerSettingsDialog, setShowOwnerSettingsDialog] = useState<boolean>(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();
    const [activePage, setActivePage] = useState<Page | null>();
    const [hoveringPage, setHoveringPage] = useState<Page>();
    const [activeChildPage, setActiveChildPage] = useState<Page>();
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const location = useLocation();
    const { t } = useTranslation();

    // Set state after fetching session to avoid UI popping issues
    const getInitialStateFromSessionState = () => {
        Session.getSessionState().then((sessionState) => {
            if (!sessionState) {
                return;
            }

            const ownerParameters = new OwnerParameters();

            const pages: Page[] = [
                {
                    title: "monitor",
                    path: "/monitor",
                    isVisible: () =>
                        ownerParameters.getOptionMonitorViewEnabled(),
                    isBeta: true,
                },
                {
                    title: "reporting.worktime",
                    path: "/worktimepage",
                    children: [
                        {
                            title: "reporting.worktime",
                            path: "/worktimepage",
                            exact: true,
                        },
                        {
                            title: "workTime.workTimeEntries",
                            path: "/worktimepage/details",
                        },
                        {
                            title: "salaries",
                            path: "/worktimepage/salaries",
                        },
                        {
                            title: "CALCULATIONSETTINGS",
                            path: "/worktimepage/calculation",
                            isVisible: () =>
                                ownerParameters.getOptionIsFunctionsAreInUse(),
                        },
                    ],
                },
                {
                    title: "planning",
                    path: "/work",
                    isVisible: () =>
                        ownerParameters.getOwnerHasWorkOrdersEnabled(),
                },
                {
                    title: "workShiftPlanning.workShiftPlanning",
                    path: "/workshiftplanning",
                    children: [
                        {
                            title: "employee.employees",
                            path: `/workshiftplanning/${WorkShiftPlanningTab.Employee}`,
                        },
                        {
                            title: "reporting.fleet",
                            path: `/workshiftplanning/${WorkShiftPlanningTab.Vehicle}`,
                        },
                        {
                            title: "route.routes",
                            path: `/workshiftplanning/${WorkShiftPlanningTab.Route}`,
                            isVisible: () =>
                                ownerParameters.getOptionRoutesEnabled(),
                        },
                    ],
                    isVisible: () =>
                        ownerParameters.getOptionWorkShiftPlanningEnabled(),
                },
                {
                    title: "telematics",
                    path: "/telematics",
                    isVisible: () => sessionState.ssoIntegrations.length > 0,
                },
                {
                    title: "fleetManagementView.topMenuTitle",
                    path: "/fleetManagementView",
                    isVisible: () => ownerParameters.getOptionOwnerHasEasyOppTelematicsEnabled(),
                    children: [
                        {
                            title: "fleetManagementView.vehicles.tabTitle",
                            path: "/fleetmanagementview/vehicles",
                            exact: true,
                        }]
                },
                {
                    title: "storage.menuStorage",
                    path: "/storage",
                    isVisible: () =>
                        ownerParameters.getOwnerHasStorageEnabled(),
                    children: [
                        {
                            title: "storage.storageProducts",
                            path: "/storage/product",
                        },
                        { title: "storage.storages", path: "/storage/storage" },
                        {
                            title: "storage.storageLocations",
                            path: "/storage/location",
                        },
                    ],
                },
                {
                    title: "transport.orders",
                    path: "/transport/orders",
                    isVisible: () => ownerParameters.getOwnerHasTransportEnabled(),
                    isBeta: true,
                },
                {
                    title: "invoicing",
                    path: "/invoicingbeta",
                    isVisible: () =>
                        ownerParameters.getOwnerHasTransportInvoicingEnabled(),
                    isBeta: true,
                    children: [
                        {
                            title: "transport.orders",
                            path: "/invoicingbeta/transportorders",
                        },
                        {
                            title: "invoices",
                            path: "/invoicingbeta/transportorderinvoices",
                        },
                    ],
                },
                {
                    title: "invoicing",
                    path: "/invoicing/workpage",
                    isVisible: () =>
                        ownerParameters.getOwnerHasWorkOrdersEnabled(),
                },
                {
                    title: "reporting.reporting",
                    path: "/reporting",
                    isVisible: () =>
                        ownerParameters.getOwnerHasReportingEnabled(),
                    children: [
                        {
                            title: "reporting.groupableWorkTime",
                            path: "/reporting/groupable",
                            isVisible: () =>
                                ownerParameters.getOptionWorkTimeGroupableReportEnabled(),
                        },
                        {
                            title: "reporting.fleet",
                            path: "/reporting/vehicles",
                            isVisible: () =>
                                ownerParameters.getOptionVehicleReportEnabled(),
                        },
                        {
                            title: "reporting.groupableFleet",
                            path: "/reporting/groupableVehicles",
                            isVisible: () =>
                                ownerParameters.getOptionVehicleReportEnabled(),
                        },
                    ],
                },
                {
                    title: "fuusorReporting",
                    path: "https://app.fuusor.fi",
                    target: "_blank",
                    isVisible: () =>
                        ownerParameters.getOptionFuusorReportingEnabled(),
                },
                { title: "tracking", path: "/tracking" },
                { title: "maintenance", path: "/settings" },
            ];

            setPages(pages);
            setSessionState(sessionState);
            setUserNames(sessionState.fullName.trim().split(" "));
            setHasPrivacyPolicy(!!ownerParameters.getPrivacyPolicy());
        }).catch(err => console.error(err));
    };

    useEffect(() => {
        if (location.pathname === "/login") {
            setIsVisible(false);
            return;
        } else if (!isVisible) {
            getInitialStateFromSessionState();
            setIsVisible(true);
        }

        if (!pages) {
            return;
        }

        const activePage = pages.find(({ path, exact }) => matchPath(location.pathname, { path, exact })) ?? null;

        setActivePage(activePage);

        if (activePage?.children) {
            setActiveChildPage(activePage.children.find(({ path, exact }) => matchPath(location.pathname, { path, exact })) ?? null);
        } else {
            setActiveChildPage(null);
        }
    }, [location, pages]);

    const handleOwnSettingsClick = () => {
        void employeeService.getOwnSettings().then(editItem => {
            setEmployeeEdit(editItem);
            setShowOwnSettingsDialog(true);
        }).catch(console.error);
    };

    const handlePrivacyPolicyClick = () => {
        if (hasPrivacyPolicy) {
            window.open(new OwnerParameters().getPrivacyPolicy(), "_blank");
        }
    };

    const handleLogOutClick = () => {
        if (!Base.isNullOrUndefined(onLogout)) {
            onLogout();
        }
    };

    const navigate = (e: SyntheticEvent, path: string) => {
        e.preventDefault(); // Prevent link navigation on normal click
        customHistory.push(path);
    };

    const onTabMouseEnter = (page: Page) => {
        setHoveringPage(page);
    };

    if (!isVisible) {
        return null;
    }

    const transportOrdersWithNotificationsEnabled = new OwnerParameters().getOwnerHasTransportEnabled() &&
        new OwnerParameters().getOwnerHasTransportNotificationsEnabled();

    return (
        <>
            <AppBar
                position="fixed"
                sx={{ bgcolor: "primary.dark", zIndex: 1001, px: 3 }}
                onMouseLeave={() => {
                    // Since mouseLeave doesn't trigger on the secondary AppBar when there are no children
                    if (!hoveringPage?.children) setHoveringPage(null);
                }}
            >
                <Toolbar disableGutters variant="dense" sx={{ height: "64px" }}>
                    <Box mr={3} >
                        <ReactLink to="/">
                            {sessionState?.ownerLogo ? (
                                <img src={"data:image/*;base64," + sessionState.ownerLogo} alt="Decorative logo" />
                            ) : (
                                <img
                                    src={appConfig.ownRoot + "EasyOpp_nega.svg"}
                                    alt="EasyOpp - Decorative logo"
                                    style={{ marginTop: 8, marginBottom: 16, height: 40 }}
                                />
                            )}
                        </ReactLink>
                    </Box>

                    {pages && (
                        <Tabs
                            className="text-decoration-none transition-color"
                            value={activePage?.path ?? false}
                            sx={{ flexGrow: 1 }}
                            variant="scrollable"
                        >
                            {pages.map((page) =>
                                !page.isVisible || page.isVisible()
                                    ? <Tab
                                            key={page.path}
                                            sx={{ opacity: 1, color: "primary.contrastText" }}
                                            disableRipple
                                            label={page.isBeta ? (<BetaLabel betaTitle={t(page.title)} />) : (t(page.title))}
                                            value={page.path}
                                            onClick={!!page.target ? undefined : (e: SyntheticEvent) => navigate(e, page.path)}
                                            component={Link}
                                            href={page.path}
                                            onMouseEnter={() => onTabMouseEnter(page)}
                                            target={page.target}
                                      />
                                    : null
                            )}
                        </Tabs>
                    )}
                    {transportOrdersWithNotificationsEnabled && (
                        <LinkIconButton
                            to={"/notifications"}
                            size="large"
                            disableRipple
                        >
                            <Notifications color="white" />
                        </LinkIconButton>
                    )}
                    <Button
                        className="flex"
                        onClick={(e) => !menuAnchorEl ? setMenuAnchorEl(e.currentTarget) : setMenuAnchorEl(null)}
                    >
                        <Avatar
                            sx={{ fontWeight: "bold", color: "primary.dark", bgcolor: "white.main", height: "30px", width: "30px", fontSize: "15px" }}
                        >
                            {`${userNames?.[0]?.[0] ?? ""}${userNames?.[1]?.[0] ?? ""}`}
                        </Avatar>
                        <Box ml={1} sx={{ display: { xs: "none", md: "block" }, textTransform: "initial" }}>
                            {`${userNames?.[0] ?? ""} ${userNames?.[1] ?? ""}`}
                        </Box>

                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={() => setMenuAnchorEl(null)}
                        >
                            <MenuItem onClick={() => setShowOwnerSettingsDialog(true)}>
                                <ListItemIcon>
                                    <Work fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{sessionState?.ownerName}</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleOwnSettingsClick}>
                                <ListItemIcon>
                                    <Settings fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{t("userSettings.ownSettings")}</ListItemText>
                            </MenuItem>
                            {hasPrivacyPolicy &&
                                <MenuItem onClick={handlePrivacyPolicyClick}>
                                    <ListItemIcon>
                                        <Policy fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>{t("privacyPolicy")}</ListItemText>
                                </MenuItem>
                            }
                            <Divider />
                            <MenuItem onClick={handleLogOutClick}>
                                <ListItemIcon>
                                    <Logout fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{t("signOut")}</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Button>

                    {showOwnSettingsDialog &&
                        <OwnSettingsDialog
                            employeeEdit={employeeEdit}
                            setEmployeeParameters={setEmployeeParameters}
                            onOk={() => setShowOwnSettingsDialog(false)}
                            onCancel={() => setShowOwnSettingsDialog(false)}
                        />
                    }
                    {showOwnerSettingsDialog &&
                        <OwnerSettingsDialog
                            onCancel={() => setShowOwnerSettingsDialog(false)}
                        />
                    }
                </Toolbar>
            </AppBar>

            <SecondaryHeader
                page={hoveringPage ? hoveringPage : activePage}
                activeChildPage={activeChildPage}
                navigate={navigate}
                onMouseLeave={() => setHoveringPage(null)}
            />
        </>
    );
}, () => {
    // Never re-render on prop change, because App.tsx renders constantly for no reason
    return true;
});

interface SecondaryHeaderProps {
    page: Page | null;
    activeChildPage: Page | null;
    navigate: (e: SyntheticEvent, route: string) => void;
    onMouseLeave: () => void;
}

const SecondaryHeader = ({ page, activeChildPage, navigate, onMouseLeave }: SecondaryHeaderProps) => {
    const { t } = useTranslation();
    const getTabsValue = () => {
        if (activeChildPage === null) {
            return false;
        }
        return page.children?.find(c => c.path === activeChildPage.path)?.path ?? false;
    };

    return (
        <Slide
            in={true}
            direction="down"
        >
            <AppBar
                position="fixed"
                sx={{ top: 64, bgcolor: "white.main", zIndex: 1000, px: 3 }}
                onMouseLeave={onMouseLeave}
            >
                {page &&
                    <Toolbar disableGutters className="navbar-secondary">
                        <Tabs
                            value={getTabsValue()}
                            variant="scrollable"
                        >
                            {page.children?.map(page =>
                                !page.isVisible || page.isVisible() ? (
                                    <Tab
                                        disableRipple
                                        className="text-decoration-none transition-color"
                                        key={page.path}
                                        label={t(page.title)}
                                        value={page.path}
                                        onClick={(e: SyntheticEvent) => navigate(e, page.path)}
                                        component={Link}
                                        href={page.path}
                                    />
                                ) : null
                            )}
                        </Tabs>
                    </Toolbar>
                }
            </AppBar>
        </Slide>
    );
};
